import React, {useState, useMemo} from 'react';
import {Link, useParams, useNavigate} from 'react-router-dom';

import {useQuery, useSubscription, useMutation} from '@apollo/client/react';
import {
  ArrowLeft,
  Users,
  Building2,
  Search,
  HardDrive,
  ChevronDown,
  ChevronUp,
  Calendar,
  Clock,
  Building,
  Phone,
  MapPin,
  Mail,
  User as UserIcon,
  X,
  UserPlus,
  Upload,
  Shield,
  Crown,
} from 'lucide-react';
import {EditOrganisationModal} from '../components/EditOrganisationModal';
import {BulkUserImportModal} from '../components/BulkUserImportModal';
import {AssignUsersToOrgModal} from '../components/AssignUsersToOrgModal';
import {ChangePermissionGroupDialog} from '../components/ChangePermissionGroupDialog';
import {GraphQLChangeRequestButton} from '../components/GraphQLChangeRequestButton';
import {DateWithRelative} from '../components/DateWithRelative';
import {OrganisationAdoption} from '../components/OrganisationAdoption';
import {SubscriptionDetailModal} from '../components/SubscriptionDetailModal';
import {Pill, PlanChips, SeatMeter, SubscriptionStatusPills} from '../components/SubscriptionBadges';
import {SubscriptionOffers} from '../components/SubscriptionOffers';
import {ActiveSubscription, DateValue, SeatAssignment} from '../types/graphql';
import {useLoginInfo} from '../graphql/localState';
import {grantableRoles, removeMemberDisabledReason, roleChangeDisabledReason} from '../graphql/roles';
// Aliased: this page keeps its own formatDate for audit timestamps (date *and* time), while
// billing periods are plain "yyyy-MM-dd" Date scalars that must be parsed as local dates.
import {
  DEFAULT_SEAT_TYPE,
  describeProductLine,
  LIGHTBOX_SEAT_TYPE,
  formatDate as formatBillingDate,
  formatMonthlyTotals,
  formatMoney,
  monthlyTotals,
  SEAT_TYPE_LABELS,
  seatNoun,
  subscriptionStatusLabel,
} from '../utils/subscriptions';
import {describeSubscriptionDiscount} from '../utils/subscriptionOffers';
import {gql} from 'types/__generated__';
import {FolderStorageUnit} from 'types/__generated__/graphql';

interface SeatHolderUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface SubscriptionHistoryRow {
  id: string;
  status: string;
  currentPeriodStart: DateValue;
  currentPeriodEnd?: DateValue | null;
  autoRenewal: boolean;
}

// `adoption` is system-admin only. It is nullable so a permission error would not null the whole
// organisation, but Apollo's default errorPolicy still discards the response on any error, so
// only select it when the logged-in user is a system admin.
const ORGANISATION_QUERY = gql(`
  query Organisation($id: UUID, $withAdoption: Boolean!) {
    organisation(id: $id) {
      id
      name
      description
      active
      numberOfUsers
      accountOwner {
        id
        username
        firstName
        lastName
      }
      roles {
        id
        name
        description
        builtIn
        assignable
        grantableByMe
        permissionIds
      }
      users {
        id
        username
        firstName
        lastName
        email
        enabled
        acceptedTerms
        createdAt
        isSSO
        organisationMemberships {
          organisationId
          isAccountOwner
          role {
            id
            name
            membersChangeableByMe
          }
        }
        workInformation {
          jobTitle
          department
          companyName
        }
        address {
          mobilePhone
          businessPhone
          city
          country
        }
      }
      teams {
        id
        name
        description
        users {
          admin
          user {
            id
            username
            firstName
            lastName
            email
          }
        }
      }
      activeSubscription {
        id
        status
        isActive
        isTrial
        isCwayTrial
        autoRenewal
        currentPeriodStart
        currentPeriodEnd
        hasModulePricing
        isManaged
        offerId
        paymentTermsDays
        discount {
          percentOff
          endsAt
        }
        entitlements {
          module
          tier
          quantity
          includedUsers
          storageQuotaGb
          trafficQuotaGb
          skuLimit
        }
        products {
          id
          name
          quantity
          priceId
          module
          tier
          addon
          seatType
          price {
            amount
            currency
          }
          discount {
            percentOff
            endsAt
          }
        }
      }
      seatUsage(seatType: PROJECT) {
        usedSeats
        includedSeats
        purchasedSeats
        totalSeats
        seatsLeft
      }
      lightboxSeatUsage: seatUsage(seatType: LIGHTBOX) {
        usedSeats
        includedSeats
        purchasedSeats
        totalSeats
        seatsLeft
      }
      requiresSeats
      seatAssignments {
        userId
        seatType
      }
      subscriptions {
        id
        status
        currentPeriodStart
        currentPeriodEnd
        autoRenewal
      }
      artworkCategories {
        id
        name
        description
        usage
      }
      adoption @include(if: $withAdoption) {
        lastActivityAt
        milestones {
          key
          achieved
          firstAchievedAt
          count
        }
      }
    }
  }
`);

const ORGANISATION_SUBSCRIPTION = gql(`
  subscription OrganisationUpdated($id: UUID!) {
    organisation(id: $id) {
      id
      name
      description
      active
      numberOfUsers
      accountOwner {
        id
        username
        firstName
        lastName
      }
      users {
        id
        username
        firstName
        lastName
        email
        enabled
        acceptedTerms
        createdAt
        isSSO
        organisationMemberships {
          organisationId
          isAccountOwner
          role {
            id
            name
            membersChangeableByMe
          }
        }
        workInformation {
          jobTitle
          department
          companyName
        }
        address {
          mobilePhone
          businessPhone
          city
          country
        }
      }
      teams {
        id
        name
        description
        users {
          admin
          user {
            id
            username
            firstName
            lastName
            email
          }
        }
      }
      subscriptions {
        id
        status
        currentPeriodStart
        currentPeriodEnd
        autoRenewal
      }
      artworkCategories {
        id
        name
        description
        usage
      }
    }
  }
`);

const ACTIVATE_ORGANISATIONS = gql(`
  mutation ActivateOrganisations($organisationIds: [UUID!]!) {
    activateOrganisations(organisationIds: $organisationIds)
  }
`);

const DEACTIVATE_ORGANISATIONS = gql(`
  mutation DeactivateOrganisations($organisationIds: [UUID!]!) {
    deactivateOrganisations(organisationIds: $organisationIds)
  }
`);

const MEDIA_CENTER_STORAGE_QUERY = gql(`
  query OrganisationMediaCenterStorage($organisationId: UUID!, $unit: FolderStorageUnit) {
    organisationMediaCenterStorageStorage(organisationId: $organisationId, unit: $unit) {
      organisationId
      organisationName
      totalSize
      fullSize
      unit
    }
  }
`);

const DELETE_USERS = gql(`
  mutation DeleteUsers($organisationId: UUID, $usernames: [String!]!) {
    deleteUsers(organisationId: $organisationId, usernames: $usernames)
  }
`);

const TRANSFER_ACCOUNT_OWNERSHIP = gql(`
  mutation TransferAccountOwnership($userId: UUID!, $organisationId: UUID) {
    transferAccountOwnership(userId: $userId, organisationId: $organisationId)
  }
`);

export default function OrganisationDetailPage() {
  const {orgId} = useParams<{orgId: string}>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [isAssignUsersModalOpen, setIsAssignUsersModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'teams' | 'subscriptions' | 'categories' | 'storage'>('users');
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [pendingActiveStatus, setPendingActiveStatus] = useState<boolean | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [showRemoveUserConfirm, setShowRemoveUserConfirm] = useState(false);
  const [userToRemove, setUserToRemove] = useState<{username: string; name: string} | null>(null);
  const [permissionDialogUser, setPermissionDialogUser] = useState<{
    username: string;
    displayName: string;
    email: string;
    currentRole: {id: string; name: string} | null;
  } | null>(null);
  const [ownerTransferUser, setOwnerTransferUser] = useState<{id: string; name: string} | null>(null);

  const loginInfo = useLoginInfo();
  const isSystemAdmin = loginInfo?.isSystemAdmin === true;
  // Ownership transfer is refused by the server from an impersonated session.
  const isImpersonating = !!loginInfo?.impersonatedBy;

  const {loading, error, data, refetch} = useQuery(ORGANISATION_QUERY, {
    variables: {id: orgId, withAdoption: isSystemAdmin},
    skip: !orgId,
  });

  const [activateOrganisations, {loading: activating}] = useMutation(ACTIVATE_ORGANISATIONS);
  const [deactivateOrganisations, {loading: deactivating}] = useMutation(DEACTIVATE_ORGANISATIONS);
  const [deleteUsers, {loading: removingUser}] = useMutation(DELETE_USERS);
  const [transferAccountOwnership, {loading: transferringOwnership}] = useMutation(TRANSFER_ACCOUNT_OWNERSHIP);

  // Query for Media Center storage
  const {
    data: storageData,
    loading: storageLoading,
    error: storageError,
  } = useQuery(MEDIA_CENTER_STORAGE_QUERY, {
    variables: {organisationId: orgId, unit: FolderStorageUnit.Gigabytes},
    skip: !orgId || activeTab !== 'storage',
  });

  // Subscribe to organisation updates
  useSubscription(ORGANISATION_SUBSCRIPTION, {
    variables: {id: orgId},
    skip: !orgId,
    onData: ({data: subscriptionData}) => {
      console.log('[OrganisationDetailPage] Subscription update received:', subscriptionData);
    },
  });

  // The organisation's own roles (built-in and custom); pickers only offer the grantable ones.
  const grantableRoleOptions = useMemo(() => grantableRoles(data?.organisation?.roles), [data?.organisation?.roles]);

  // This page's query is untyped (see the other tabs); type the billing slice we render here.
  const activeSubscription: ActiveSubscription | null = data?.organisation?.activeSubscription ?? null;

  const seatHolders: {user: SeatHolderUser; assignment: SeatAssignment}[] = useMemo(() => {
    const organisation = data?.organisation;
    if (!organisation?.seatAssignments?.length || !organisation?.users) return [];
    const usersById = new Map<string, SeatHolderUser>(organisation.users.map((user: SeatHolderUser) => [user.id, user]));
    return (organisation.seatAssignments as SeatAssignment[]).flatMap((assignment) => {
      const user = usersById.get(assignment.userId);
      return user ? [{user, assignment}] : [];
    });
  }, [data]);

  // Lightbox seats are sold by sales only: most organisations have none, and then say nothing about them.
  const lightboxSeatUsage = data?.organisation?.lightboxSeatUsage;
  const showLightboxSeats =
    !!lightboxSeatUsage && (lightboxSeatUsage.totalSeats > 0 || seatHolders.some(({assignment}) => assignment.seatType === LIGHTBOX_SEAT_TYPE));

  const handleStatusToggle = (newStatus: boolean) => {
    setPendingActiveStatus(newStatus);
    setShowStatusConfirm(true);
  };

  const confirmStatusChange = async () => {
    if (pendingActiveStatus === null || !orgId) return;

    try {
      if (pendingActiveStatus) {
        await activateOrganisations({variables: {organisationIds: [orgId]}});
      } else {
        await deactivateOrganisations({variables: {organisationIds: [orgId]}});
      }
      await refetch();
      setShowStatusConfirm(false);
      setPendingActiveStatus(null);
    } catch (err) {
      console.error('Error changing organisation status:', err);
    }
  };

  const cancelStatusChange = () => {
    setShowStatusConfirm(false);
    setPendingActiveStatus(null);
  };

  const org = data?.organisation;

  // Helper function to format dates
  const formatDate = (timestamp: number | null | undefined) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return (
      date.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'}) +
      ' at ' +
      date.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'})
    );
  };

  // Helper function to format relative time
  const formatRelativeTime = (timestamp: number | null | undefined) => {
    if (!timestamp) return 'Never';
    const now = Date.now();
    const diff = now - timestamp;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  };

  const toggleUserExpanded = (userId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleRemoveUserClick = (username: string, firstName: string, lastName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setUserToRemove({
      username,
      name: `${firstName} ${lastName}` || username,
    });
    setShowRemoveUserConfirm(true);
  };

  const handlePermissionBadgeClick = (user: any, event: React.MouseEvent) => {
    event.stopPropagation();
    const membership = user.organisationMemberships?.find((m: any) => m.organisationId === orgId);
    setPermissionDialogUser({
      username: user.username,
      displayName: `${user.firstName} ${user.lastName}`,
      email: user.email,
      currentRole: membership?.role ? {id: membership.role.id, name: membership.role.name} : null,
    });
  };

  const handleTransferOwnershipClick = (user: {id: string; firstName: string; lastName: string; username: string}, event: React.MouseEvent) => {
    event.stopPropagation();
    setOwnerTransferUser({id: user.id, name: `${user.firstName} ${user.lastName}`.trim() || user.username});
  };

  const confirmTransferOwnership = async () => {
    if (!ownerTransferUser || !orgId) return;
    try {
      await transferAccountOwnership({variables: {userId: ownerTransferUser.id, organisationId: orgId}});
      setOwnerTransferUser(null);
      await refetch();
    } catch (err: any) {
      console.error('Error transferring account ownership:', err);
      alert(`Failed to transfer account ownership: ${err.message}`);
    }
  };

  const confirmRemoveUser = async () => {
    if (!userToRemove || !orgId) return;

    try {
      await deleteUsers({
        variables: {
          organisationId: orgId,
          usernames: [userToRemove.username],
        },
      });
      setShowRemoveUserConfirm(false);
      setUserToRemove(null);
      // Refetch to update the user list
      await refetch();
    } catch (err: any) {
      console.error('Error removing user from organisation:', err);
      alert(`Failed to remove user: ${err.message}`);
    }
  };

  const cancelRemoveUser = () => {
    setShowRemoveUserConfirm(false);
    setUserToRemove(null);
  };

  // Group users by email domain and filter by search
  const groupedUsers = useMemo(() => {
    if (!org?.users) return {};

    const filtered = org.users.filter((user) => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        user.firstName?.toLowerCase().includes(search) ||
        user.lastName?.toLowerCase().includes(search) ||
        user.username?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search) ||
        user.workInformation?.jobTitle?.toLowerCase().includes(search) ||
        user.workInformation?.department?.toLowerCase().includes(search)
      );
    });

    // Group by email domain
    const groups: Record<string, typeof filtered> = {};
    filtered.forEach((user) => {
      const domain = user.email?.split('@')[1] || 'Unknown';
      if (!groups[domain]) {
        groups[domain] = [];
      }
      groups[domain].push(user);
    });

    // Sort domains alphabetically
    return Object.keys(groups)
      .sort()
      .reduce(
        (acc, domain) => {
          acc[domain] = groups[domain].sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''));
          return acc;
        },
        {} as Record<string, typeof filtered>,
      );
  }, [org?.users, searchTerm]);

  const totalFilteredUsers = useMemo(() => Object.values(groupedUsers).reduce((sum, users) => sum + users.length, 0), [groupedUsers]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h3 className="text-red-800 dark:text-red-200 font-semibold">Error loading organisation</h3>
          <p className="text-red-600 dark:text-red-300 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <h3 className="text-yellow-800 dark:text-yellow-200 font-semibold">Organisation not found</h3>
          <p className="text-yellow-600 dark:text-yellow-300 mt-1">The organisation you're looking for doesn't exist.</p>
          <button onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate('/admin/organisations')} className="mt-4 text-blue-600 dark:text-blue-400 hover:underline">
            Back to Organisations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate('/admin/organisations')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Organisation Details</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">View and manage organisation information</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Status Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
            <button
              onClick={() => handleStatusToggle(!org.active)}
              disabled={activating || deactivating}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                org.active ? 'bg-green-600 focus:ring-green-500' : 'bg-gray-300 dark:bg-gray-600 focus:ring-gray-400'
              } ${activating || deactivating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title={org.active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  org.active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${org.active ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
              {org.active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <GraphQLChangeRequestButton />
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            title="Edit organisation"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Organisation Profile Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start space-x-4">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Building2 size={32} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{org.name}</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">{org.description || 'No description available'}</p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
              <span className="font-medium">ID:</span> {org.id}
            </p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-500 flex items-center gap-1.5">
              <Crown size={14} className="text-amber-500" />
              <span className="font-medium">Account owner:</span>
              {org.accountOwner ? (
                <button
                  onClick={() => navigate(`/admin/users/${org.accountOwner!.username}`)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                  title={org.accountOwner.username}
                >
                  {`${org.accountOwner.firstName} ${org.accountOwner.lastName}`.trim() || org.accountOwner.username}
                </button>
              ) : (
                <span className="italic">none - pick a member below</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Adoption funnel (system admins only) */}
      {isSystemAdmin && <OrganisationAdoption adoption={org.adoption} loading={loading} />}

      {/* Tabbed Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        {/* Tab Headers */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex space-x-1 px-6">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'users'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Users ({org.users?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('teams')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'teams'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Teams ({org.teams?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'subscriptions'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Subscription &amp; seats
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'categories'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Categories ({org.artworkCategories?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('storage')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'storage'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <div className="flex items-center space-x-2">
                <HardDrive size={16} />
                <span>Storage</span>
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'users' && (
          <>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="relative flex-1">
                  <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users by name, email, title, department..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => setIsAssignUsersModalOpen(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                  title="Add existing users from the system to this organization"
                >
                  <UserPlus size={18} />
                  <span className="hidden sm:inline">Add Existing Users</span>
                </button>
                <button
                  onClick={() => setIsBulkImportModalOpen(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                  title="Import new users from email list"
                >
                  <Upload size={18} />
                  <span className="hidden sm:inline">Import from List</span>
                </button>
              </div>
            </div>
            {/* Warning banner when users are limited */}
            {org.numberOfUsers && org.users && org.users.length < org.numberOfUsers && (
              <div className="mx-6 mt-4 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-orange-600 dark:text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-orange-800 dark:text-orange-300">Limited User Display</p>
                    <p className="mt-1 text-sm text-orange-700 dark:text-orange-400">
                      Showing {org.users.length} of {org.numberOfUsers} total users. The GraphQL API currently limits the number of users returned.{' '}
                      <Link to={`/admin/users?org=${orgId}`} className="font-medium underline hover:no-underline">
                        See all {org.numberOfUsers} in the user list
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="p-6">
              {totalFilteredUsers > 0 ? (
                <div className="space-y-6">
                  {Object.entries(groupedUsers).map(([domain, users]) => (
                    <div key={domain}>
                      {/* Domain Header */}
                      <div className="flex items-center mb-3">
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
                        <h4 className="px-4 text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                          {domain} ({users.length})
                        </h4>
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
                      </div>

                      {/* Users in Domain */}
                      <div className="space-y-2">
                        {users.map((user) => {
                          const isExpanded = expandedUsers.has(user.id);
                          const membership = user.organisationMemberships?.find((m) => m.organisationId === orgId);
                          const memberGate = {isAccountOwner: !!membership?.isAccountOwner, role: membership?.role};
                          const roleChangeReason = roleChangeDisabledReason(memberGate, isSystemAdmin);
                          const removeReason = removeMemberDisabledReason(memberGate, isSystemAdmin);
                          return (
                            <div
                              key={user.id}
                              className={`rounded-lg overflow-hidden transition-colors ${
                                user.enabled
                                  ? 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                                  : 'bg-gray-100 dark:bg-gray-800/70 opacity-50 border border-gray-300 dark:border-gray-700'
                              }`}
                            >
                              {/* Main user card */}
                              <div
                                className={`flex items-center justify-between p-4 transition-colors cursor-pointer ${
                                  user.enabled ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : 'hover:bg-gray-200 dark:hover:bg-gray-800'
                                }`}
                                onClick={() => navigate(`/admin/users/${user.username}`)}
                              >
                                <div className="flex items-center space-x-3 flex-1">
                                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-blue-600 dark:text-blue-400 font-semibold">
                                      {user.firstName?.[0]}
                                      {user.lastName?.[0]}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                    {/* Left column: Name and basic info */}
                                    <div className="min-w-0">
                                      <p className="font-medium text-gray-900 dark:text-gray-100">
                                        {user.firstName} {user.lastName}
                                        {user.isSSO && (
                                          <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                                            SSO
                                          </span>
                                        )}
                                        {!user.enabled && (
                                          <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
                                            Disabled
                                          </span>
                                        )}
                                      </p>
                                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{user.email}</p>
                                    </div>
                                    {/* Right column: Job info, role, and created date */}
                                    <div className="min-w-0 hidden sm:block">
                                      <div className="space-y-0.5">
                                        {user.workInformation?.jobTitle && (
                                          <p className="text-xs text-gray-900 dark:text-gray-100 truncate">{user.workInformation.jobTitle}</p>
                                        )}
                                        {user.workInformation?.department && (
                                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.workInformation.department}</p>
                                        )}
                                        {(() => {
                                          const roleName = membership?.role?.name;
                                          return (
                                            <div className="flex flex-wrap items-center gap-1">
                                              <button
                                                onClick={(e) => handlePermissionBadgeClick(user, e)}
                                                disabled={!!roleChangeReason}
                                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors disabled:cursor-not-allowed ${
                                                  roleName
                                                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800/60 ring-1 ring-blue-200 dark:ring-blue-700'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 ring-1 ring-gray-200 dark:ring-gray-600'
                                                }`}
                                                title={roleChangeReason ?? 'Change role'}
                                              >
                                                <Shield size={12} />
                                                {roleName || 'Set role'}
                                              </button>
                                              {membership?.isAccountOwner && (
                                                <span
                                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-700"
                                                  title="Account owner: billing and seat purchasing"
                                                >
                                                  <Crown size={12} />
                                                  Account owner
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })()}
                                        <div className="text-xs text-gray-500 dark:text-gray-400">Created {formatRelativeTime(user.createdAt)}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {isSystemAdmin && org.accountOwner?.id !== user.id && (
                                    <button
                                      onClick={(e) => handleTransferOwnershipClick(user, e)}
                                      disabled={isImpersonating}
                                      className="p-2 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                      aria-label="Make account owner"
                                      title={isImpersonating ? 'Not available while impersonating another user' : 'Make account owner'}
                                    >
                                      <Crown size={18} />
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => handleRemoveUserClick(user.username, user.firstName, user.lastName, e)}
                                    disabled={!!removeReason}
                                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                    aria-label="Remove user from organisation"
                                    title={removeReason ?? 'Remove from organisation'}
                                  >
                                    <X size={18} />
                                  </button>
                                  <button
                                    onClick={(e) => toggleUserExpanded(user.id, e)}
                                    className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                    aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                                  >
                                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                  </button>
                                </div>
                              </div>

                              {/* Expanded details */}
                              {isExpanded && (
                                <div className="px-4 pb-4 pt-2 border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Left column */}
                                    <div className="space-y-3">
                                      <div className="flex items-start space-x-2">
                                        <Calendar size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Created</p>
                                          <p className="text-sm text-gray-900 dark:text-gray-100">{formatDate(user.createdAt)}</p>
                                          <p className="text-xs text-gray-500">{formatRelativeTime(user.createdAt)}</p>
                                        </div>
                                      </div>
                                      {/* TODO: Add lastLogin field when backend supports it in organisation context */}
                                      {/* <div className="flex items-start space-x-2">
                                    <Clock size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Last Login</p>
                                      <p className="text-sm text-gray-900 dark:text-gray-100">Not available</p>
                                      <p className="text-xs text-gray-500">Requires backend support</p>
                                    </div>
                                  </div> */}
                                      <div className="flex items-start space-x-2">
                                        <UserIcon size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Username</p>
                                          <p className="text-sm text-gray-900 dark:text-gray-100 font-mono">{user.username}</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Right column */}
                                    <div className="space-y-3">
                                      {user.workInformation?.companyName && (
                                        <div className="flex items-start space-x-2">
                                          <Building size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Company</p>
                                            <p className="text-sm text-gray-900 dark:text-gray-100">{user.workInformation.companyName}</p>
                                          </div>
                                        </div>
                                      )}
                                      {(user.address?.mobilePhone || user.address?.businessPhone) && (
                                        <div className="flex items-start space-x-2">
                                          <Phone size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Phone</p>
                                            {user.address.mobilePhone && (
                                              <p className="text-sm text-gray-900 dark:text-gray-100">Mobile: {user.address.mobilePhone}</p>
                                            )}
                                            {user.address.businessPhone && (
                                              <p className="text-sm text-gray-900 dark:text-gray-100">Business: {user.address.businessPhone}</p>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      {(user.address?.city || user.address?.country) && (
                                        <div className="flex items-start space-x-2">
                                          <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Location</p>
                                            <p className="text-sm text-gray-900 dark:text-gray-100">
                                              {[user.address.city, user.address.country].filter(Boolean).join(', ')}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                      <div className="flex items-start space-x-2">
                                        <Mail size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</p>
                                          <div className="flex flex-wrap gap-2 mt-1">
                                            {user.acceptedTerms && (
                                              <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                                                Terms Accepted
                                              </span>
                                            )}
                                            {user.enabled ? (
                                              <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                                                Enabled
                                              </span>
                                            ) : (
                                              <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
                                                Disabled
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : org.users && org.users.length > 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">No users match your search</div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">No users in this organisation</div>
              )}
            </div>
          </>
        )}

        {/* Teams Tab */}
        {activeTab === 'teams' && (
          <div className="p-6">
            {org.teams && org.teams.length > 0 ? (
              <div className="space-y-4">
                {org.teams.map((team) => (
                  <div key={team.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{team.name}</h4>
                        {team.description && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{team.description}</p>}
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {team.users.length} member{team.users.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {team.users.length > 0 && (
                      <div className="space-y-2">
                        {team.users.map((teamUser) => (
                          <div
                            key={teamUser.user.id}
                            className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                            onClick={() => navigate(`/admin/users/${teamUser.user.username}`)}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                                <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">
                                  {teamUser.user.firstName?.[0]}
                                  {teamUser.user.lastName?.[0]}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {teamUser.user.firstName} {teamUser.user.lastName}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400">{teamUser.user.email}</p>
                              </div>
                            </div>
                            {teamUser.admin && (
                              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded">
                                Admin
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">No teams in this organisation</div>
            )}
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div className="p-6 space-y-6">
            {/* Current plan */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Current plan</h4>
                <button
                  onClick={() => setIsSubscriptionModalOpen(true)}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Manage subscription
                </button>
              </div>

              {activeSubscription?.isActive ? (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <SubscriptionStatusPills subscription={activeSubscription} />
                    {activeSubscription.discount && <Pill tone="teal">{describeSubscriptionDiscount(activeSubscription.discount)}</Pill>}
                    {activeSubscription.offerId && (
                      <Pill tone="blue" title={`From offer ${activeSubscription.offerId}`}>
                        from special offer
                      </Pill>
                    )}
                  </div>

                  <PlanChips subscription={activeSubscription} />

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-gray-200 dark:border-gray-600">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Period start</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatBillingDate(activeSubscription.currentPeriodStart)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {activeSubscription.isTrial ? 'Trial ends' : 'Period ends'}
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatBillingDate(activeSubscription.currentPeriodEnd)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Monthly total</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {activeSubscription.isTrial ? '—' : formatMonthlyTotals(monthlyTotals(activeSubscription.products))}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Auto renewal</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {activeSubscription.autoRenewal ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                  </div>

                  {activeSubscription.products.length > 0 && (
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-white dark:bg-gray-800 text-xs uppercase text-gray-500 dark:text-gray-400">
                          <tr>
                            <th className="px-3 py-2 font-medium">Item</th>
                            <th className="px-3 py-2 font-medium">Kind</th>
                            <th className="px-3 py-2 font-medium text-right">Qty</th>
                            <th className="px-3 py-2 font-medium text-right">Unit / month</th>
                            <th className="px-3 py-2 font-medium">Deal</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
                          {activeSubscription.products.map((product) => (
                            <tr key={`${product.id}::${product.priceId || ''}`}>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{product.name}</td>
                              <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                {describeProductLine(product, activeSubscription)}
                              </td>
                              <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-right">{product.quantity}</td>
                              <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-right whitespace-nowrap">
                                {/* Trial products carry a display-only 0 price — a rendered $0 reads as a real price. */}
                                {activeSubscription.isTrial ? '—' : formatMoney(product.price.amount, product.price.currency)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {product.discount ? (
                                  <span className="text-green-700 dark:text-green-400">{describeSubscriptionDiscount(product.discount)}</span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <p className="text-xs font-mono text-gray-400 dark:text-gray-500 break-all">{activeSubscription.id}</p>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 text-sm text-gray-600 dark:text-gray-300">
                  No active subscription
                  {activeSubscription && ` (last status: ${subscriptionStatusLabel(activeSubscription)})`} — use Manage subscription to start
                  a trial.
                </div>
              )}
            </section>

            {/* Sales offers */}
            <SubscriptionOffers organisationId={org.id} organisationName={org.name} />

            {/* Seats */}
            {(org.seatUsage || showLightboxSeats) && (
              <section>
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                  {org.seatUsage && showLightboxSeats
                    ? 'Seats'
                    : seatNoun(org.seatUsage ? DEFAULT_SEAT_TYPE : LIGHTBOX_SEAT_TYPE, {capital: true, plural: true})}
                </h4>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 space-y-3">
                  {org.seatUsage && (
                    <>
                      <SeatMeter seatUsage={org.seatUsage} />
                      {!org.requiresSeats && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          This organisation is not seat-gated — members are never blocked by seat limits.
                        </p>
                      )}
                    </>
                  )}
                  {showLightboxSeats && lightboxSeatUsage && (
                    <div className={org.seatUsage ? 'pt-3 border-t border-gray-200 dark:border-gray-600 space-y-3' : 'space-y-3'}>
                      <SeatMeter seatUsage={lightboxSeatUsage} seatType={LIGHTBOX_SEAT_TYPE} />
                    </div>
                  )}
                  {seatHolders.length > 0 && (
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-white dark:bg-gray-800 text-xs uppercase text-gray-500 dark:text-gray-400">
                          <tr>
                            <th className="px-3 py-2 font-medium">Member</th>
                            <th className="px-3 py-2 font-medium">Email</th>
                            <th className="px-3 py-2 font-medium">Seat</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
                          {seatHolders.map(({user, assignment}) => (
                            <tr key={`${user.id}:${assignment.seatType}`}>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                {user.firstName} {user.lastName}
                              </td>
                              <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{user.email}</td>
                              <td className="px-3 py-2">
                                <Pill tone="teal" square>
                                  {SEAT_TYPE_LABELS[assignment.seatType]}
                                </Pill>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Subscription history */}
            {org.subscriptions && org.subscriptions.length > 0 && (
              <section>
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">History</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400">
                      <tr>
                        <th className="px-3 py-2 font-medium">Subscription</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium">Period</th>
                        <th className="px-3 py-2 font-medium">Auto renewal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {(org.subscriptions as SubscriptionHistoryRow[]).map((sub) => (
                        <tr key={sub.id} className={sub.id === activeSubscription?.id ? 'bg-blue-50/50 dark:bg-blue-900/10' : undefined}>
                          <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-300 break-all">{sub.id}</td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{sub.status.toLowerCase()}</td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            {formatBillingDate(sub.currentPeriodStart)} – {formatBillingDate(sub.currentPeriodEnd)}
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{sub.autoRenewal ? 'Enabled' : 'Disabled'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="p-6">
            {org.artworkCategories && org.artworkCategories.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {org.artworkCategories.map((category) => (
                  <div key={category.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">{category.name}</h4>
                    {category.description && <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{category.description}</p>}
                    {category.usage !== null && category.usage !== undefined && (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Used in</span>
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded">
                          {category.usage} project{category.usage !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">No categories defined for this organisation</div>
            )}
          </div>
        )}

        {/* Storage Tab */}
        {activeTab === 'storage' && (
          <div className="p-6">
            {storageLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {storageError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-800 dark:text-red-200 font-semibold">Error loading storage information</p>
                <p className="text-red-600 dark:text-red-300 text-sm mt-1">{storageError.message}</p>
              </div>
            )}

            {!storageLoading && !storageError && storageData?.organisationMediaCenterStorageStorage && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <HardDrive size={32} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Media Center Storage</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Storage usage for this organisation</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Total Size</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {storageData.organisationMediaCenterStorageStorage.totalSize} {storageData.organisationMediaCenterStorageStorage.unit}
                      </p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Full Size</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {storageData.organisationMediaCenterStorageStorage.fullSize} {storageData.organisationMediaCenterStorageStorage.unit}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <strong>Organisation ID:</strong> {storageData.organisationMediaCenterStorageStorage.organisationId}
                    </p>
                    {storageData.organisationMediaCenterStorageStorage.organisationName && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        <strong>Organisation Name:</strong> {storageData.organisationMediaCenterStorageStorage.organisationName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">About Storage</h4>
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    Storage metrics show how much space is being used by Media Center files for this organisation. The total size represents
                    compressed storage, while full size shows uncompressed file sizes.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Remove User Confirmation Modal */}
      {showRemoveUserConfirm && userToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Remove User from Organisation</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to remove <strong>{userToRemove.name}</strong> from <strong>{org.name}</strong>?
              <span className="block mt-2 text-orange-600 dark:text-orange-400">
                This will revoke their access to this organisation and all its projects.
              </span>
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelRemoveUser}
                disabled={removingUser}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveUser}
                disabled={removingUser}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {removingUser ? 'Removing...' : 'Remove User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Confirmation Modal */}
      {showStatusConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Confirm Status Change</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to {pendingActiveStatus ? 'activate' : 'deactivate'} <strong>{org.name}</strong>?
              {!pendingActiveStatus && (
                <span className="block mt-2 text-orange-600 dark:text-orange-400">
                  Deactivating will prevent users from accessing this organisation.
                </span>
              )}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelStatusChange}
                disabled={activating || deactivating}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                disabled={activating || deactivating}
                className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${
                  pendingActiveStatus ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {activating || deactivating ? 'Processing...' : pendingActiveStatus ? 'Activate' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {org && (
        <EditOrganisationModal
          organisation={org}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            // The subscription will handle updating the UI
          }}
        />
      )}

      {/* Subscription & seats management */}
      {org && isSubscriptionModalOpen && (
        <SubscriptionDetailModal
          organisationId={org.id}
          organisationName={org.name}
          onClose={() => setIsSubscriptionModalOpen(false)}
          onChanged={() => refetch()}
        />
      )}

      {/* Bulk User Import Modal */}
      {org && orgId && (
        <BulkUserImportModal
          organisationId={orgId}
          organisationName={org.name}
          isOpen={isBulkImportModalOpen}
          onClose={() => setIsBulkImportModalOpen(false)}
          onSuccess={() => {
            // Refetch to show newly imported users
            refetch();
          }}
        />
      )}

      {/* Assign Existing Users Modal */}
      <AssignUsersToOrgModal
        organisationId={orgId || ''}
        organisationName={org?.name || ''}
        isOpen={isAssignUsersModalOpen}
        onClose={() => setIsAssignUsersModalOpen(false)}
        onSuccess={() => {
          // Refetch to show newly assigned users
          refetch();
          setIsAssignUsersModalOpen(false);
        }}
      />

      {/* Change Permission Group Dialog */}
      {permissionDialogUser && (
        <ChangePermissionGroupDialog
          isOpen={!!permissionDialogUser}
          onClose={() => setPermissionDialogUser(null)}
          onSuccess={() => refetch()}
          username={permissionDialogUser.username}
          userDisplayName={permissionDialogUser.displayName}
          userEmail={permissionDialogUser.email}
          organisationId={orgId!}
          currentRole={permissionDialogUser.currentRole}
          roles={grantableRoleOptions}
        />
      )}

      {/* Transfer account ownership confirmation */}
      {ownerTransferUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={() => setOwnerTransferUser(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Crown size={20} className="text-amber-500" />
              Transfer account ownership
            </h3>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              Make <strong>{ownerTransferUser.name}</strong> the account owner of <strong>{org.name}</strong>?
              {org.accountOwner && (
                <>
                  {' '}
                  This replaces {`${org.accountOwner.firstName} ${org.accountOwner.lastName}`.trim() || org.accountOwner.username} immediately.
                </>
              )}{' '}
              The account owner holds billing and seat purchasing on top of their role.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setOwnerTransferUser(null)}
                disabled={transferringOwnership}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmTransferOwnership}
                disabled={transferringOwnership}
                className="px-4 py-2 text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
              >
                {transferringOwnership ? 'Transferring...' : 'Transfer ownership'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
